import React, { useState, useEffect, useRef } from 'react';
import { get, post, put } from '../../utils/apiClient';
import { clearCacheKey } from '../../utils/cacheUtils';
import Base64Image from '../Base64Image';
import {
  AdminSectionTitle,
  AdminSectionText,
  AdminLoginLabel,
  AdminLoginInput,
  AdminLoginButton,
  AdminButton,
  AdminSaveButton,
} from '../../styles/AdminPageStyles';

const AdminAcademyPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [uploadingEstablishment, setUploadingEstablishment] = useState(false);
  const [uploadingFacility, setUploadingFacility] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const [newFacility, setNewFacility] = useState({
    title: '',
    description: '',
    albumName: '',
    category: '',
    displayOrder: '',
    file: null,
    files: [],
  });
  const facilityFileInputRef = useRef(null);

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await get('/academy');
        setPageData(data);
      } catch (err) {
        setError(err && err.message ? err.message : 'Failed to load academy page.');
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, []);

  const handleSimpleFieldChange = (field, value) => {
    setPageData((prev) => ({
      ...(prev || {}),
      [field]: value,
    }));
  };

  const updateMissionItem = (index, value) => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const items = Array.isArray(next.missionItems) ? [...next.missionItems] : [];
      const existing = items[index] || {};
      items[index] = {
        ...existing,
        text: value,
      };
      next.missionItems = items;
      return next;
    });
  };

  const addMissionItem = () => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const items = Array.isArray(next.missionItems) ? [...next.missionItems] : [];
      items.push({ text: '' });
      next.missionItems = items;
      return next;
    });
  };

  const removeMissionItem = (index) => {
    setPageData((prev) => {
      if (!prev || !Array.isArray(prev.missionItems)) return prev;
      const items = prev.missionItems.filter((_, i) => i !== index);
      return {
        ...prev,
        missionItems: items,
      };
    });
  };

  const updateActivityItem = (index, value) => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const items = Array.isArray(next.activityItems) ? [...next.activityItems] : [];
      const existing = items[index] || {};
      items[index] = {
        ...existing,
        text: value,
      };
      next.activityItems = items;
      return next;
    });
  };

  const addActivityItem = () => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const items = Array.isArray(next.activityItems) ? [...next.activityItems] : [];
      items.push({ text: '' });
      next.activityItems = items;
      return next;
    });
  };

  const removeActivityItem = (index) => {
    setPageData((prev) => {
      if (!prev || !Array.isArray(prev.activityItems)) return prev;
      const items = prev.activityItems.filter((_, i) => i !== index);
      return {
        ...prev,
        activityItems: items,
      };
    });
  };

  const handleEstablishmentTextChange = (e) => {
    const { value } = e.target;
    setPageData((prev) => ({
      ...(prev || {}),
      establishmentStory: {
        ...((prev && prev.establishmentStory) || {}),
        text: value,
      },
    }));
  };

  const handleEnrollmentTextChange = (e) => {
    const { value } = e.target;
    setPageData((prev) => ({
      ...(prev || {}),
      enrollmentInfo: {
        ...((prev && prev.enrollmentInfo) || {}),
        text: value,
      },
    }));
  };

  const handleMissionTextareaChange = (e) => {
    const { value } = e.target;
    const lines = value
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    setPageData((prev) => ({
      ...(prev || {}),
      missionItems: lines.map((text) => ({ text })),
    }));
  };

  const handleActivitiesTextareaChange = (e) => {
    const { value } = e.target;
    const lines = value
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    setPageData((prev) => ({
      ...(prev || {}),
      activityItems: lines.map((text) => ({ text })),
    }));
  };

  const handleContactInfoFieldChange = (field, value) => {
    setPageData((prev) => ({
      ...(prev || {}),
      contactInfo: {
        ...((prev && prev.contactInfo) || {}),
        [field]: value,
      },
    }));
  };

  const updateProgram = (index, field, value) => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const programs = Array.isArray(next.programs) ? [...next.programs] : [];
      const existing = programs[index] || {};
      programs[index] = {
        ...existing,
        [field]: value,
      };
      next.programs = programs;
      return next;
    });
  };

  const addProgram = () => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const programs = Array.isArray(next.programs) ? [...next.programs] : [];
      programs.push({ title: '', description: '' });
      next.programs = programs;
      return next;
    });
  };

  const removeProgram = (index) => {
    setPageData((prev) => {
      if (!prev || !Array.isArray(prev.programs)) return prev;
      const programs = prev.programs.filter((_, i) => i !== index);
      return {
        ...prev,
        programs,
      };
    });
  };

  const handleHeaderBackgroundUpload = async (file) => {
    if (!file) return;
    setUploadingHeader(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);
      await post('/academy/header/background-image', formData);
      const data = await get('/academy');
      setPageData(data);
      clearCacheKey('academy_data');
    } catch (err) {
      setUploadError(err && err.message ? err.message : 'Failed to upload header background.');
    } finally {
      setUploadingHeader(false);
    }
  };

  const handleEstablishmentImageUpload = async (file) => {
    if (!file) return;
    setUploadingEstablishment(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('image', file);
      await post('/academy/establishment-story/image', formData);
      const data = await get('/academy');
      setPageData(data);
      clearCacheKey('academy_data');
    } catch (err) {
      setUploadError(err && err.message ? err.message : 'Failed to upload establishment story image.');
    } finally {
      setUploadingEstablishment(false);
    }
  };

  const handleNewFacilityFieldChange = (field, value) => {
    setNewFacility((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNewFacilityFileChange = (e) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setNewFacility((prev) => ({
      ...prev,
      file: files[0] || null,
      files,
    }));
  };

  const handleFacilitiesUpload = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const filesToUpload =
      newFacility.files && newFacility.files.length > 0
        ? newFacility.files
        : newFacility.file
        ? [newFacility.file]
        : [];

    if (filesToUpload.length === 0) {
      setUploadError('Please select at least one image file for the facility.');
      return;
    }

    setUploadingFacility(true);
    setUploadError(null);

    try {
      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append('image', file);
        if (newFacility.title) formData.append('title', newFacility.title);
        if (newFacility.description) formData.append('description', newFacility.description);
        if (newFacility.albumName) formData.append('albumName', newFacility.albumName);
        if (newFacility.category) formData.append('category', newFacility.category);
        if (newFacility.displayOrder !== '') {
          formData.append('displayOrder', newFacility.displayOrder);
        }

        await post('/academy/facilities-gallery/upload', formData);
      }
      const data = await get('/academy');
      setPageData(data);
      clearCacheKey('academy_data');
      setNewFacility({
        title: '',
        description: '',
        albumName: '',
        category: '',
        displayOrder: '',
        file: null,
        files: [],
      });
      if (facilityFileInputRef.current) {
        facilityFileInputRef.current.value = '';
      }
    } catch (err) {
      setUploadError(err && err.message ? err.message : 'Failed to upload facility image.');
    } finally {
      setUploadingFacility(false);
    }
  };

  const updateFacility = (index, field, value) => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const facilities = Array.isArray(next.facilitiesGallery)
        ? [...next.facilitiesGallery]
        : [];
      const existing = facilities[index] || {};
      facilities[index] = {
        ...existing,
        [field]: value,
      };
      next.facilitiesGallery = facilities;
      return next;
    });
  };

  const addFacility = () => {
    setPageData((prev) => {
      const next = { ...(prev || {}) };
      const facilities = Array.isArray(next.facilitiesGallery)
        ? [...next.facilitiesGallery]
        : [];
      facilities.push({ title: '', description: '' });
      next.facilitiesGallery = facilities;
      return next;
    });
  };

  const removeFacility = (index) => {
    setPageData((prev) => {
      if (!prev || !Array.isArray(prev.facilitiesGallery)) return prev;
      const facilities = prev.facilitiesGallery.filter((_, i) => i !== index);
      return {
        ...prev,
        facilitiesGallery: facilities,
      };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!pageData) return;

    setSaving(true);
    setError(null);
    setSaveSuccess(null);

    try {
      const payload = {
        header: {
          ...(pageData.header || {}),
          title:
            (pageData.header && pageData.header.title) ||
            'Academy / Refuge Home',
        },
        overviewText: pageData.overviewText || '',
        establishmentStory: pageData.establishmentStory || {},
        historyText: pageData.historyText || '',
        missionItems: Array.isArray(pageData.missionItems)
          ? pageData.missionItems
          : [],
        activityItems: Array.isArray(pageData.activityItems)
          ? pageData.activityItems
          : [],
        programs: Array.isArray(pageData.programs) ? pageData.programs : [],
        facilitiesGallery: Array.isArray(pageData.facilitiesGallery)
          ? pageData.facilitiesGallery
          : [],
        enrollmentInfo: pageData.enrollmentInfo || {},
        contactInfo: pageData.contactInfo || {},
      };

      const updated = await put('/academy', payload);
      setPageData(updated);
      setSaveSuccess('Academy page saved successfully.');
      clearCacheKey('academy_data');
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to save academy page.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !pageData) {
    return (
      <>
        <AdminSectionTitle>Academy / Refuge Home CMS</AdminSectionTitle>
        <AdminSectionText>Loading academy page...</AdminSectionText>
      </>
    );
  }

  const header = pageData && pageData.header ? pageData.header : null;
  const establishmentStory = pageData ? pageData.establishmentStory : null;

  return (
    <>
      <AdminSectionTitle>Academy / Refuge Home CMS</AdminSectionTitle>

      <form onSubmit={handleSave}>
        <div style={{ marginBottom: 16 }}>
          <AdminSectionTitle style={{ fontSize: '1rem', marginBottom: 8 }}>
            Header Background Image
          </AdminSectionTitle>
          {header && header.backgroundImage && (
            <div style={{ marginBottom: 8 }}>
              <Base64Image
                image={header.backgroundImage}
                alt={header.title || 'Academy header'}
                style={{ width: 260, borderRadius: 6 }}
              />
            </div>
          )}
          <AdminLoginLabel htmlFor="academy-header-bg">Upload Header Background</AdminLoginLabel>
          <input
            id="academy-header-bg"
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
              if (file) {
                handleHeaderBackgroundUpload(file);
                e.target.value = '';
              }
            }}
            style={{ fontSize: '0.9rem' }}
          />
          {uploadingHeader && (
            <AdminSectionText style={{ fontSize: '0.85rem' }}>
              Uploading header background...
            </AdminSectionText>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="overviewText">Overview Text</AdminLoginLabel>
          <textarea
            id="overviewText"
            rows={3}
            value={(pageData && pageData.overviewText) || ''}
            onChange={(e) => handleSimpleFieldChange('overviewText', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="establishmentText">Establishment Story</AdminLoginLabel>
          <textarea
            id="establishmentText"
            rows={3}
            value={(pageData && pageData.establishmentStory && pageData.establishmentStory.text) || ''}
            onChange={handleEstablishmentTextChange}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
          {establishmentStory && establishmentStory.image && (
            <div style={{ marginTop: 8, marginBottom: 8 }}>
              <Base64Image
                image={establishmentStory.image}
                alt="Establishment story"
                style={{ width: 220, borderRadius: 6 }}
              />
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <AdminLoginLabel htmlFor="academy-establishment-image">
              Upload Establishment Story Image
            </AdminLoginLabel>
            <input
              id="academy-establishment-image"
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                if (file) {
                  handleEstablishmentImageUpload(file);
                  e.target.value = '';
                }
              }}
              style={{ fontSize: '0.9rem' }}
            />
            {uploadingEstablishment && (
              <AdminSectionText style={{ fontSize: '0.85rem' }}>
                Uploading establishment story image...
              </AdminSectionText>
            )}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="historyText">History Text</AdminLoginLabel>
          <textarea
            id="historyText"
            rows={3}
            value={(pageData && pageData.historyText) || ''}
            onChange={(e) => handleSimpleFieldChange('historyText', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="enrollmentText">Enrollment Information</AdminLoginLabel>
          <textarea
            id="enrollmentText"
            rows={3}
            value={(pageData && pageData.enrollmentInfo && pageData.enrollmentInfo.text) || ''}
            onChange={handleEnrollmentTextChange}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

        <hr style={{ margin: '16px 0' }} />

        <AdminSectionTitle>Mission Items</AdminSectionTitle>
        <AdminSectionText style={{ marginBottom: 8 }}>
          Enter one mission per line. Each line will be displayed as a separate mission on the school page.
        </AdminSectionText>
        <textarea
          id="mission-items-textarea"
          rows={5}
          value={
            pageData && Array.isArray(pageData.missionItems)
              ? pageData.missionItems
                  .map((item) => (item && item.text ? item.text : ''))
                  .filter((text) => text !== '')
                  .join('\n')
              : ''
          }
          onChange={handleMissionTextareaChange}
          style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
        />

        <hr style={{ margin: '16px 0' }} />

        <AdminSectionTitle>Activities</AdminSectionTitle>
        <AdminSectionText style={{ marginBottom: 8 }}>
          Enter one activity per line. Each line will be displayed as a separate activity on the school page.
        </AdminSectionText>
        <textarea
          id="activity-items-textarea"
          rows={5}
          value={
            pageData && Array.isArray(pageData.activityItems)
              ? pageData.activityItems
                  .map((item) => (item && item.text ? item.text : ''))
                  .filter((text) => text !== '')
                  .join('\n')
              : ''
          }
          onChange={handleActivitiesTextareaChange}
          style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
        />

        <hr style={{ margin: '16px 0' }} />

        <AdminSectionTitle>School Contact Information</AdminSectionTitle>
        <AdminSectionText style={{ marginBottom: 8 }}>
          Enter the contact information to display on the school page.
        </AdminSectionText>

        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="school-address">School Address</AdminLoginLabel>
          <textarea
            id="school-address"
            rows={2}
            value={(pageData && pageData.contactInfo && pageData.contactInfo.schoolAddress) || ''}
            onChange={(e) => handleContactInfoFieldChange('schoolAddress', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="contact-numbers">Contact Numbers</AdminLoginLabel>
          <AdminLoginInput
            id="contact-numbers"
            value={(pageData && pageData.contactInfo && pageData.contactInfo.contactNumbers) || ''}
            onChange={(e) => handleContactInfoFieldChange('contactNumbers', e.target.value)}
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <AdminLoginLabel htmlFor="contact-email">Email (optional)</AdminLoginLabel>
          <AdminLoginInput
            id="contact-email"
            type="email"
            value={(pageData && pageData.contactInfo && pageData.contactInfo.email) || ''}
            onChange={(e) => handleContactInfoFieldChange('email', e.target.value)}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <AdminLoginLabel htmlFor="contact-other-details">Other Details (optional)</AdminLoginLabel>
          <textarea
            id="contact-other-details"
            rows={2}
            value={(pageData && pageData.contactInfo && pageData.contactInfo.otherDetails) || ''}
            onChange={(e) => handleContactInfoFieldChange('otherDetails', e.target.value)}
            style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
          />
        </div>

        <hr style={{ margin: '16px 0' }} />

        <AdminSectionTitle>Programs</AdminSectionTitle>
        <AdminSectionText style={{ marginBottom: 8 }}>
          Manage the list of academy programs (title and description).
        </AdminSectionText>

        {pageData && Array.isArray(pageData.programs) && pageData.programs.map((program, index) => (
          <div
            key={index}
            style={{
              border: '1px solid #ddd',
              borderRadius: 8,
              padding: 10,
              marginBottom: 8,
            }}
          >
            <div style={{ marginBottom: 8 }}>
              <AdminLoginLabel htmlFor={`program-title-${index}`}>
                Title
              </AdminLoginLabel>
              <AdminLoginInput
                id={`program-title-${index}`}
                value={program.title || ''}
                onChange={(e) => updateProgram(index, 'title', e.target.value)}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <AdminLoginLabel htmlFor={`program-description-${index}`}>
                Description
              </AdminLoginLabel>
              <textarea
                id={`program-description-${index}`}
                rows={3}
                value={program.description || ''}
                onChange={(e) => updateProgram(index, 'description', e.target.value)}
                style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
              />
            </div>
            <AdminButton type="button" onClick={() => removeProgram(index)}>
              Remove Program
            </AdminButton>
          </div>
        ))}

        <div style={{ marginBottom: 16 }}>
          <AdminButton type="button" onClick={addProgram}>
            Add Program
          </AdminButton>
        </div>

        <hr style={{ margin: '16px 0' }} />

        <AdminSectionTitle>Facilities</AdminSectionTitle>
        <AdminSectionText style={{ marginBottom: 8 }}>
          Manage the facilities gallery entries (title, description, and display order).
        </AdminSectionText>

        {pageData && Array.isArray(pageData.facilitiesGallery) && pageData.facilitiesGallery.map((item, index) => (
          <div
            key={index}
            style={{
              border: '1px solid #ddd',
              borderRadius: 8,
              padding: 10,
              marginBottom: 8,
            }}
          >
            {item.image && (
              <div style={{ marginBottom: 8 }}>
                <Base64Image
                  image={item.image}
                  alt={item.title || 'Facility image'}
                  style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 4 }}
                />
              </div>
            )}
            <div style={{ marginBottom: 8 }}>
              <AdminLoginLabel htmlFor={`facility-title-${index}`}>
                Title
              </AdminLoginLabel>
              <AdminLoginInput
                id={`facility-title-${index}`}
                value={item.title || ''}
                onChange={(e) => updateFacility(index, 'title', e.target.value)}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <AdminLoginLabel htmlFor={`facility-description-${index}`}>
                Description
              </AdminLoginLabel>
              <textarea
                id={`facility-description-${index}`}
                rows={3}
                value={item.description || ''}
                onChange={(e) => updateFacility(index, 'description', e.target.value)}
                style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
              />
            </div>
            <div style={{ marginBottom: 8 }}>
              <AdminLoginLabel htmlFor={`facility-order-${index}`}>
                Display Order (optional)
              </AdminLoginLabel>
              <AdminLoginInput
                id={`facility-order-${index}`}
                type="number"
                value={
                  typeof item.displayOrder === 'number'
                    ? item.displayOrder
                    : ''
                }
                onChange={(e) => updateFacility(index, 'displayOrder', e.target.value === '' ? undefined : Number(e.target.value))}
              />
            </div>
            <AdminButton type="button" onClick={() => removeFacility(index)}>
              Remove Facility
            </AdminButton>
          </div>
        ))}

        <div style={{ marginBottom: 24 }}>
          <AdminButton type="button" onClick={addFacility}>
            Add Facility
          </AdminButton>
        </div>

        <div style={{ marginBottom: 24 }}>
          <AdminSectionTitle style={{ fontSize: '0.95rem', marginBottom: 8 }}>
            Add Facility Image
          </AdminSectionTitle>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="new-facility-title">Title (optional)</AdminLoginLabel>
            <AdminLoginInput
              id="new-facility-title"
              value={newFacility.title}
              onChange={(e) => handleNewFacilityFieldChange('title', e.target.value)}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="new-facility-description">Description (optional)</AdminLoginLabel>
            <textarea
              id="new-facility-description"
              rows={2}
              value={newFacility.description}
              onChange={(e) => handleNewFacilityFieldChange('description', e.target.value)}
              style={{ width: '100%', padding: 8, fontSize: '0.9rem' }}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="new-facility-album">Album Name (optional)</AdminLoginLabel>
            <AdminLoginInput
              id="new-facility-album"
              value={newFacility.albumName}
              onChange={(e) => handleNewFacilityFieldChange('albumName', e.target.value)}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="new-facility-category">Category (optional)</AdminLoginLabel>
            <AdminLoginInput
              id="new-facility-category"
              value={newFacility.category}
              onChange={(e) => handleNewFacilityFieldChange('category', e.target.value)}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="new-facility-order">Display Order (optional)</AdminLoginLabel>
            <AdminLoginInput
              id="new-facility-order"
              type="number"
              value={newFacility.displayOrder}
              onChange={(e) => handleNewFacilityFieldChange('displayOrder', e.target.value)}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <AdminLoginLabel htmlFor="new-facility-file">Image File(s)</AdminLoginLabel>
            <input
              id="new-facility-file"
              type="file"
              accept="image/*"
              multiple
              onChange={handleNewFacilityFileChange}
              ref={facilityFileInputRef}
              style={{ fontSize: '0.9rem' }}
            />
          </div>
          <AdminLoginButton
            type="button"
            onClick={handleFacilitiesUpload}
            disabled={uploadingFacility}
          >
            {uploadingFacility ? 'Uploading...' : 'Upload Facility Image'}
          </AdminLoginButton>
        </div>

        <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', gap: '16px' }}>
          <AdminSaveButton type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Academy Page'}
          </AdminSaveButton>
          {saveSuccess && (
            <span style={{ color: '#1b5e20', fontWeight: '500' }}>{saveSuccess}</span>
          )}
          {error && (
            <span style={{ color: '#b71c1c', fontWeight: '500' }}>{error}</span>
          )}
          {uploadError && (
            <span style={{ color: '#b71c1c', fontWeight: '500' }}>{uploadError}</span>
          )}
        </div>
      </form>
    </>
  );
};

export default AdminAcademyPage;
